import { ArrowLeft, CheckCircle2, DoorClosed, Lock, Plus } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SignOutButton } from "@clerk/clerk-react";
import { clerkEnabled } from "@/services/clerk";
import { REDIRECT_PARAM, afterSignOut, safeRedirect } from "@/lib/redirects";
import { useMe } from "@/features/members/hooks/useMe";
import { useUpdateProfile } from "@/features/members/hooks/useProfileMutations";
import {
  useCreateOrganization,
  useFundedOrganizations,
  useLeaveOrganization,
  useRequestOrgSponsorship,
} from "@/features/members/hooks/useOrganizations";
import {
  useMyInvites,
  useRespondToInvite,
} from "@/features/members/hooks/useInvites";
import {
  usePublicConfig,
  useSubmitApplication,
} from "@/features/applications/hooks/useApplications";
import { AfellMark } from "@/components/AfellMark";
import { OrganizationPicker } from "@/components/OrganizationPicker";
import { EmailChoice } from "@/components/EmailChoice";
import { EmailAddresses } from "@/components/profile/EmailAddresses";
import { LinksEditor, type LinkRow } from "@/components/profile/LinksEditor";
import { MediaEditor, type MediaRow } from "@/components/profile/MediaEditor";
import { ChoiceChips } from "@/components/ChoiceChips";
import { useInviteRelationship } from "@/components/InviteRelationship";
import { FormSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

/** The two reasons a fee can be waived. They match the seeded form's choices. */

/**
 * The two fee-waiver reasons, exactly as `GIFT_REQUEST_OPTIONS` seeds them.
 *
 * Compared byte-for-byte on the server, so this is not copy to improve: the
 * first is matched to decide that a request is a coverage claim rather than a
 * gift. Change one and change `lib/seeds.py` with it.
 */
const WAIVER_REASONS = [
  "Someone else from my organization has a paid membership",
  "I need help covering the cost",
] as const;

/**
 * Applying to be listed, in three steps.
 *
 * **Step one is the profile itself, not a copy of it.** Saving writes straight
 * through to the member's profile, so the profile is always current and the
 * reviewer reads the live version — there is no submitted copy to diverge from
 * it, and nothing to overwrite at approval. Approval fills only what is still
 * blank.
 *
 * **Step two is the organizations**, on the same principle: it mirrors the
 * directory rather than collecting a copy of it. Joining entries that already
 * exist is immediate and needs nobody's permission — two stewards of one estate
 * finding each other is what the directory is for. Creating a *new* entity is a
 * claim about a real body, so it waits, and the application approves it: one
 * decision about a person and the estate they steward. One new entity per
 * application, which is the form's own long-standing rule — register each
 * artist or entity separately.
 *
 * **Step three is the part that is not directory data**: the consents, and a
 * fee-waiver request. Money is not asked about at all — that moved to the
 * billing page, which unlocks once an application is approved.
 *
 * Needs an account, because it writes to a profile. Signing up grants one.
 */
export default function ApplyPage() {
  const { data: config, isPending: configPending, error } = usePublicConfig();
  const { data: me, isPending: mePending } = useMe(clerkEnabled);
  const { pathname } = useLocation();
  const member = me?.member ?? null;
  const pending = me?.application?.status === "pending";
  const [step, setStep] = useState<1 | 2 | 3>(1);
  // Step one owns unsaved text, so leaving it goes through the step itself:
  // it saves what is in the fields and then moves, rather than dropping it.
  const leaveProfile = useRef<((target: 1 | 2 | 3) => void) | null>(null);
  const [profileReady, setProfileReady] = useState(false);

  function goToStep(target: 1 | 2 | 3) {
    if (target === step) return;
    if (step === 1 && leaveProfile.current) {
      leaveProfile.current(target);
      return;
    }
    setStep(target);
  }

  // Both vocabularies come from the seeded form definition, so an admin editing
  // the choices in /basement/ still reaches applicants without a release.
  const fieldFor = (name: string) =>
    config?.form.fields.find((f) => f.internal_name === name);
  const choicesFor = (name: string) => fieldFor(name)?.choices ?? [];
  const wordLimit = (name: string) => {
    const limit = fieldFor(name)?.validation_rules?.word_limit;
    return typeof limit === "number" ? limit : null;
  };

  if (configPending || mePending) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-16">
        <FormSkeleton sections={2} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-12">
      {/* The mark, not a back button: this page has no sidebar to carry it,
          and a form is somewhere you sit down rather than somewhere you
          browse from. It is still the way home.

          Somebody *updating* a registration is the exception, and is why an
          explicit way back appears below for them. They did not come here to
          fill a form in, they came to change one line, and the mark alone
          reads as a logo rather than as an exit. Todd, after the 1 September
          demo: "a way back from the update application page (that isn't just
          the logo click)." */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Link to="/" className="inline-flex items-center gap-2" aria-label="AFELL">
          <AfellMark className="size-8" />
          <span className="font-heading text-lg">AFELL</span>
        </Link>
        {/* Who you are, and the way out. The page sits outside the shell, so
            it carries no identity menu: somebody who signed in with the wrong
            Google account had no way to notice and no way to change it without
            first navigating somewhere else to find one. Naming the address
            matters more than the link does, because the mistake is silent. */}
        {clerkEnabled && me?.email && (
          <p className="text-muted-foreground text-sm">
            <span className="break-all">{me.email}</span>{" "}
            <SignOutButton redirectUrl={afterSignOut(pathname)}>
              <button
                type="button"
                className="text-foreground underline underline-offset-4"
              >
                Sign out
              </button>
            </SignOutButton>
          </p>
        )}
      </div>

      <div className="mt-8">
        <h1 className="text-3xl tracking-[-0.015em]">Membership Registration</h1>
        {/* Somebody who has already sent this in is here to change something,
            not to be told what registering is. */}
        {pending ? (
          <>
            <p className="mt-4 text-base leading-relaxed">
              Your registration is with an administrator. You can change anything
              here while it waits, and what they read is whatever it says when
              they get to it.
            </p>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to="/">
                <ArrowLeft data-slot="icon" /> Back to AFELL
              </Link>
            </Button>
          </>
        ) : (
          <p className="mt-4 text-base leading-relaxed">
            Membership is for those active in legacy planning for themselves, a
            specific artist or community, and for representatives of an
            artist&apos;s legacy entity: an estate, foundation, trust or studio.
            Registering puts you in front of an administrator for review;
            payment comes afterwards, from your billing page.
          </p>
        )}
      </div>

      {error || !config ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Couldn&apos;t load the registration form</CardTitle>
            <CardDescription>
              Please try again in a moment. If it keeps happening, email{" "}
              <a
                href="mailto:hello@softnetwork.art"
                className="underline underline-offset-4"
              >
                hello@softnetwork.art
              </a>
              .
            </CardDescription>
          </CardHeader>
        </Card>
      ) : !config.applicationsOpen ? (
        <Card className="animate-page-enter mt-6">
          <CardHeader>
            <DoorClosed className="text-muted-foreground mb-2 size-8" />
            <CardTitle>Applications are closed</CardTitle>
            <CardDescription>{config.applicationsClosedMessage}</CardDescription>
          </CardHeader>
        </Card>
      ) : !member ? (
        <SignInFirst />
      ) : member.listed ? (
        <AlreadyListed />
      ) : (
        <>
          <Steps
            step={step}
            onSelect={goToStep}
            // Forward steps open once the thing they depend on exists: a
            // reviewer needs the profile, and step three's waiver question is
            // about the organizations named in step two.
            reachable={{
              1: true,
              2: step !== 1 || profileReady,
              // Step three no longer waits on a legacy: not every member
              // represents one, and requiring it only got the box filled in.
              3: step !== 1 || profileReady,
            }}
          />
          {step === 1 ? (
            <ProfileStep
              bioWordLimit={wordLimit("statement")}
              onSaved={(target) => setStep(target)}
              bindLeave={(fn) => {
                leaveProfile.current = fn;
              }}
              onReadyChange={setProfileReady}
            />
          ) : step === 2 ? (
            <OrganizationStep
              relationships={choicesFor("relationship")}
              mediums={choicesFor("mediums")}
              onBack={() => setStep(1)}
              onDone={() => setStep(3)}
            />
          ) : (
            <WaiverStep onBack={() => setStep(2)} pending={pending} />
          )}
        </>
      )}
    </div>
  );
}

/** Applying writes to a profile, so it needs an account.
 *
 *  The route already sends somebody with no session to sign in, so this is the
 *  narrow case of a session whose member row has not arrived — and the way out
 *  of it is the same one door. */
function SignInFirst() {
  return (
    <Card className="animate-page-enter mt-6">
      <CardHeader>
        <Lock className="text-muted-foreground mb-2 size-8" />
        <CardTitle>Sign in first</CardTitle>
        <CardDescription>
          Registering fills in your directory profile, so it needs an account to
          fill in. Signing in with an address we do not know yet makes one, and
          costs nothing.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button asChild>
          <Link to="/sign-in">Sign in</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function AlreadyListed() {
  return (
    <Card className="animate-page-enter mt-6">
      <CardHeader>
        <CheckCircle2 className="mb-2 size-8 text-green-600" />
        <CardTitle>You&apos;re already listed</CardTitle>
        <CardDescription>
          Your application has been approved, so there is nothing more to
          register. Membership options are on your billing page.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <Link to="/billing">Membership &amp; billing</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * The three steps, and a way back to any of them.
 *
 * Registering is not a funnel: somebody who remembers a second estate while
 * reading the consents should be able to go back for it and return, without
 * retyping a description on the way. Leaving step one saves it first.
 */
function Steps({
  step,
  onSelect,
  reachable,
}: {
  step: 1 | 2 | 3;
  onSelect: (target: 1 | 2 | 3) => void;
  reachable: Record<1 | 2 | 3, boolean>;
}) {
  // One word each. Every label truncated on a phone, "Legacies" included: the
  // three buttons split the width evenly and the numbered badge took forty of
  // the remaining pixels, leaving about sixty for text. "Fees & policies" was
  // also the wrong promise for a step that deliberately charges nothing.
  const labels: Record<1 | 2 | 3, string> = {
    1: "Profile",
    2: "Organizations",
    3: "Membership",
  };
  return (
    // Three buttons that look like buttons. They were underlined grey text at
    // 12px, which read as a caption rather than as the way through the form —
    // and the whole point of them is that the steps are navigable.
    <nav
      aria-label="Registration steps"
      className="mt-8 grid grid-cols-3 gap-2"
    >
      {([1, 2, 3] as const).map((n) => (
        <button
          key={n}
          type="button"
          aria-current={step === n ? "step" : undefined}
          disabled={!reachable[n]}
          onClick={() => onSelect(n)}
          className={
            // px-2 on a phone: measured, "Membership" needs the eight pixels
            // that buys and fits from 360 up. Below that it clips, which is a
            // 2016 SE and nothing newer.
            "flex items-center gap-2 rounded-md border px-2 py-2.5 text-left text-sm transition-colors sm:px-3 disabled:cursor-not-allowed disabled:opacity-50 " +
            (step === n
              ? "border-brand bg-primary/10 font-medium"
              : "border-border hover:bg-muted")
          }
          title={reachable[n] ? undefined : "Fill in what a reviewer needs first"}
        >
          {/* Hidden on a phone, where it is the difference between a readable
              label and "Legac…". The order of the buttons and the one that is
              highlighted already say which step this is, so the number is
              decoration exactly where there is no room for it. */}
          <span
            aria-hidden
            className={
              "hidden size-6 shrink-0 items-center justify-center rounded-full text-xs sm:flex " +
              (step === n
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground")
            }
          >
            {n}
          </span>
          <span className="truncate">{labels[n]}</span>
        </button>
      ))}
    </nav>
  );
}

/**
 * Step one: the profile, edited in place.
 *
 * A mirror of the profile page rather than a copy of its data — saving here is
 * saving there. Which is what makes "the profile is always up to date with the
 * application" true rather than aspirational, and what lets the reviewer read
 * the live profile even if the applicant edits it a week after submitting.
 *
 * The optional profile fields (photo, links, media, IP notes) are deliberately
 * absent: this asks for what a review needs, and the rest can be filled in
 * afterwards from the profile page.
 */
function ProfileStep({
  bioWordLimit,
  onSaved,
  bindLeave,
  onReadyChange,
}: {
  bioWordLimit: number | null;
  onSaved: (target: 1 | 2 | 3) => void;
  bindLeave: (fn: ((target: 1 | 2 | 3) => void) | null) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  const { data: me } = useMe(clerkEnabled);
  const updateProfile = useUpdateProfile();
  const member = me?.member ?? null;

  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [directoryEmail, setDirectoryEmail] = useState("");
  const [listservEmail, setListservEmail] = useState("");
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [media, setMedia] = useState<MediaRow[]>([]);

  useEffect(() => {
    if (!member) return;
    // A fresh sign-up's member is named after its email until they say
    // otherwise; that is not a name to prefill.
    setName(member.name === me?.email ? "" : member.name ?? "");
    setLocation(member.location ?? "");
    setBio(member.bio ?? "");
    setLinks(member.links ?? []);
    setMedia((member.media ?? []) as MediaRow[]);
    setDirectoryEmail(member.directory_email || me?.email || "");
    setListservEmail(member.listserv_email ?? "");
  }, [member?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Counted the same way the server counts it, so the two agree about whether a
  // description is too long.
  const bioWords = bio.trim() ? bio.trim().split(/\s+/).length : 0;
  const overLimit = bioWordLimit !== null && bioWords > bioWordLimit;

  const filled =
    !overLimit &&
    name.trim() &&
    location.trim() &&
    bio.trim() &&
    directoryEmail.trim();

  // Kept fresh on every render so the handler closes over the current fields:
  // clicking "3. Fees & policies" from here saves what is typed and then moves,
  // rather than navigating away from unsaved text.
  const dirty =
    !member ||
    name.trim() !== (member.name === me?.email ? "" : member.name) ||
    location.trim() !== (member.location ?? "") ||
    bio.trim() !== (member.bio ?? "") ||
    directoryEmail.trim() !== (member.directory_email || me?.email || "") ||
    listservEmail.trim() !== (member.listserv_email ?? "");

  useEffect(() => {
    onReadyChange(Boolean(filled));
    bindLeave((target) => {
      if (!filled) return; // the gap notice below already says what is missing
      // Nothing typed since the last save: move without a pointless write and
      // without a "Profile saved" toast for a profile nobody changed.
      if (!dirty) {
        onSaved(target);
        return;
      }
      save(target);
    });
    return () => bindLeave(null);
  });

  function save(target: 1 | 2 | 3 = 2) {
    updateProfile.mutate(
      {
        name: name.trim(),
        location: location.trim(),
        bio: bio.trim(),
        directory_email: directoryEmail.trim(),
        listserv_email: listservEmail.trim(),
        links: links.filter((l) => l.label.trim() && l.url.trim()),
        media: media.filter((m) => m.url.trim()),
        // Organizations are step two now. Nothing here removes one, so no
        // coverage can be at risk — but the flag keeps a save from being
        // refused if they arrived with an organization already covering them.
        confirm_losing_coverage: true,
      },
      {
        onSuccess: () => {
          toast.success("Profile saved");
          onSaved(target);
        },
        onError: (e) => toast.error(e.message),
      }
    );
  }

  return (
    <div className="animate-page-enter mt-4 space-y-4">
      {/* The section's copy, once, above the boxes it is about. */}
      <p className="text-foreground/75 text-sm leading-relaxed">
        This step <em>is</em> your profile, so saving here saves it, and
        everything on it is published in the AFELL directory. An administrator
        reads whatever it says when they get to it, so you can come back and
        change any of this at any time, before or after approval.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* One box, matching the profile this step writes to: first/last
              captured none of "J. Q. Public", "María del Carmen Ruiz" or
              "Lee Chen (Chen Lok Lee)". */}
          <div className="space-y-2">
            <Label htmlFor="apply-name">Member Name *</Label>
            <Input
              id="apply-name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apply-location">
              Geographic Location (City, State, Country) *
            </Label>
            <Input
              id="apply-location"
              placeholder="City, State, Country"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apply-bio">Bio ({bioWordLimit} words max) *</Label>
            <Textarea
              id="apply-bio"
              rows={5}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
            {bioWordLimit !== null && (
              <p
                className={
                  overLimit
                    ? "text-destructive text-xs"
                    : "text-muted-foreground text-xs"
                }
              >
                {bioWords}/{bioWordLimit} words
                {overLimit ? ", too long to submit" : ""}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* The account's addresses, here rather than a link to the Account page:
          the next box asks which one to publish, and somebody whose only
          address is the one they signed up with has nowhere to add the shared
          info@ they want in the directory. Sending them to another page in
          the middle of a form is how a form gets abandoned. */}
      <EmailAddresses />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Contact</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <EmailChoice
            id="apply-directory-email"
            label="Member email for AFELL directory *"
            value={directoryEmail}
            onChange={setDirectoryEmail}
          />
          <EmailChoice
            id="apply-listserv-email"
            label="Listserv email, if different"
            value={listservEmail}
            onChange={setListservEmail}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Links</CardTitle>
          <CardDescription>Website, socials, press.</CardDescription>
        </CardHeader>
        <CardContent>
          <LinksEditor value={links} onChange={setLinks} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Media</CardTitle>
          <CardDescription>
            Images, audio, or video shown on your profile (paste URLs).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MediaEditor value={media} onChange={setMedia} />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={!filled || updateProfile.isPending}
          onClick={() => save(2)}
        >
          {updateProfile.isPending && <Spinner data-slot="icon" />}
          Save and continue
        </Button>
        {!filled && (
          <span className="text-muted-foreground text-sm">
            A reviewer needs your name, where you are, a bio and an address.
          </span>
        )}
      </div>
    </div>
  );
}


/**
 * Step two: the organizations, mirroring the directory rather than copying it.
 *
 * Two different acts, deliberately shaped differently. **Joining** an entry
 * that already exists is immediate: two stewards of one estate landing on one
 * row is the whole point of the directory, and a queue in front of that would
 * fork it back into two. **Creating** a new entity is a claim about a real body,
 * so it waits for an administrator — and for an applicant that review is the
 * application itself.
 *
 * As many as they steward. The old registration form told anyone looking after
 * several estates to file a separate application for each, which is why 36 of
 * the imported applications are flagged multi-entity: one person, several
 * estates, split across rows that later had to be merged back together.
 */
function OrganizationStep({
  relationships,
  mediums,
  onBack,
  onDone,
}: {
  relationships: string[];
  mediums: string[];
  onBack: () => void;
  onDone: () => void;
}) {
  const { data: me } = useMe(clerkEnabled);
  const create = useCreateOrganization();
  const leave = useLeaveOrganization();
  const invites = useMyInvites(clerkEnabled);
  const respond = useRespondToInvite();
  const member = me?.member ?? null;

  const [creating, setCreating] = useState(false);
  const [newOrg, setNewOrg] = useState<{
    name: string;
    description: string;
    location: string;
    website: string;
    mediums: string[];
    mediums_other: string;
    relationship: string[];
    relationship_other: string;
  }>({
    name: "",
    description: "",
    location: "",
    website: "",
    mediums: [],
    mediums_other: "",
    relationship: [],
    relationship_other: "",
  });

  const mine = member?.organizations ?? [];
  const filed = mine.filter((o) => o.status === "pending");
  const received = invites.data?.received ?? [];


  return (
    <Card className="animate-page-enter mt-4">
      <CardHeader>
        <CardTitle className={sectionTitleClass}>
          The artists and entities you steward
        </CardTitle>
        <CardDescription className="text-foreground/75 text-sm leading-relaxed">
          Add every artist or entity you steward. Each one is reviewed with
          your registration and shared with anyone else who stewards it. An
          organization already in the directory is joined by invitation: ask one of
          its stewards to invite you, and it will appear on this step. Nothing
          here holds up your registration, and you can skip this step if you
          work in legacy planning without representing anyone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {mine.length > 0 && (
          <ul className="space-y-2">
            {mine.map((organization) => (
              <li
                key={organization.id}
                className="space-y-2 rounded-md border px-3 py-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex flex-wrap items-center gap-2">
                    {organization.name}
                    {organization.status === "pending" && (
                      <Badge variant="secondary">Awaiting review</Badge>
                    )}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={leave.isPending}
                    onClick={() =>
                      leave.mutate(
                        { id: organization.id, confirm: true },
                        { onError: (e) => toast.error(e.message) }
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {received.length > 0 && (
          <div className="space-y-2 rounded-md border p-3">
            <p className="text-sm font-medium">
              {received.length === 1
                ? "You have been invited to steward an organization"
                : `You have been invited to steward ${received.length} organizations`}
            </p>
            {received.map((invite) => (
              <div
                key={invite.id}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <span className="text-sm">
                  {invite.organization.name}
                  <span className="text-muted-foreground">
                    {" "}
                    {invite.invited_by || "a steward"} invited you
                  </span>
                </span>
                <InviteAccept
                  invite={invite}
                  relationships={relationships}
                  busy={respond.isPending}
                  onAccept={(relationship) =>
                    respond.mutate(
                      { id: invite.id, action: "accept", relationship },
                      { onError: (e) => toast.error(e.message) }
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}

        {filed.length > 0 && !creating && (
          <p className="text-muted-foreground rounded-md border p-3 text-sm">
            {filed.length === 1 ? (
              <>
                <strong>{filed[0].name}</strong> joins the directory when your
                application is approved.
              </>
            ) : (
              <>
                <strong>
                  {filed.map((o) => o.name).join(", ")}
                </strong>{" "}
                join the directory when your application is approved.
              </>
            )}{" "}
            Add another below if you steward more.
          </p>
        )}
        {creating ? (
          <div className="space-y-3 rounded-md border p-3">
            <div className="space-y-2">
              <Label htmlFor="apply-new-org">Artist or Legacy Entity Name *</Label>
              <OrganizationPicker
                id="apply-new-org"
                value={newOrg.name}
                onChange={(name) => setNewOrg({ ...newOrg, name })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apply-new-desc">Description</Label>
              <Textarea
                id="apply-new-desc"
                rows={3}
                value={newOrg.description}
                onChange={(e) =>
                  setNewOrg({ ...newOrg, description: e.target.value })
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="apply-new-loc">Geographic Location (City, State, Country)</Label>
                <Input
                  id="apply-new-loc"
                  value={newOrg.location}
                  onChange={(e) =>
                    setNewOrg({ ...newOrg, location: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apply-new-web">Artist or Legacy Entity Website</Label>
                <Input
                  id="apply-new-web"
                  value={newOrg.website}
                  onChange={(e) =>
                    setNewOrg({ ...newOrg, website: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mediums in which the artist works/worked</Label>
              <ChoiceChips
                options={mediums}
                value={newOrg.mediums}
                onChange={(next) => setNewOrg({ ...newOrg, mediums: next })}
                other={newOrg.mediums_other}
                onOtherChange={(v) => setNewOrg({ ...newOrg, mediums_other: v })}
              />
            </div>
            {/* Asked here as well as on the row, because this is the moment
                somebody is describing their connection to the entity — and
                because a question that only appears *after* the thing exists
                reads as a question that was dropped. */}
            <div className="space-y-2">
              <Label>Your relationship to the artist or entity *</Label>
              <ChoiceChips
                options={relationships}
                value={newOrg.relationship}
                onChange={(next) => setNewOrg({ ...newOrg, relationship: next })}
                other={newOrg.relationship_other}
                onOtherChange={(v) =>
                  setNewOrg({ ...newOrg, relationship_other: v })
                }
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button
                disabled={
                  !newOrg.name.trim() ||
                  newOrg.relationship.length === 0 ||
                  create.isPending
                }
                onClick={() =>
                  create.mutate(
                    {
                      name: newOrg.name.trim(),
                      description: newOrg.description,
                      location: newOrg.location,
                      website: newOrg.website,
                      mediums: newOrg.mediums,
                      mediums_other: newOrg.mediums_other,
                      // Rides along on creation, so nobody says it twice.
                      relationship: newOrg.relationship,
                      relationship_other: newOrg.relationship_other,
                    },
                    {
                      onSuccess: (organization) => {
                        toast.success(
                          organization.joined
                            ? `You now steward ${organization.name}`
                            : `${organization.name} will be reviewed with your application`
                        );
                        setCreating(false);
                        setNewOrg({
                          name: "",
                          description: "",
                          location: "",
                          website: "",
                          mediums: [],
                          mediums_other: "",
                          relationship: [],
                          relationship_other: "",
                        });
                      },
                      onError: (e) => toast.error(e.message),
                    }
                  )
                }
              >
                {create.isPending && <Spinner data-slot="icon" />}
                Add organization
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus data-slot="icon" />{" "}
            Add Organization
          </Button>
        )}
      </CardContent>
      <CardFooter className="flex-wrap justify-between gap-2">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        {/* Nothing to hold them here. What they are to each legacy is asked
            where they join it — accepting an invitation, or filing a new one
            below — so there is no unanswered question on this page to block. */}
        <Button
          onClick={onDone}
        >
          Continue
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * The two answers a legacy needs on the application: what this member is to it,
 * and what the artist worked in.
 *
 * They go to different places and so they are written separately. The
 * relationship belongs to the *link* — somebody can be the daughter of one
 * artist and the studio manager for another — and the mediums belong to the
 * *entity*, which is why the directory's medium filter can be built from them.
 * Both used to ride in a profile save that rewrote every link the member had,
 * which one declined legacy in the list was enough to refuse.
 */
/**
 * An invitation, and the one question that has to be answered to take it.
 *
 * Relationship belongs to the link, so this is where it is asked: joining
 * without one makes a link that cannot say what it is.
 */
function InviteAccept({
  invite,
  relationships,
  busy,
  onAccept,
}: {
  invite: { organization: { name: string } };
  relationships: string[];
  busy: boolean;
  onAccept: (relationship: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rel = useInviteRelationship();
  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Accept
      </Button>
    );
  }
  return (
    <div className="w-full space-y-3 rounded-md border p-3">
      {rel.field(relationships)}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={busy || !rel.ready}
          onClick={() => onAccept(rel.relationship)}
        >
          Accept {invite.organization.name}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function PolicyLink({ href, children }: { href?: string; children: ReactNode }) {
  if (!href) return <>{children}</>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      // The label wraps a checkbox, so a click inside it would tick the box on
      // the way to the policy.
      onClick={(e) => e.stopPropagation()}
      className="underline underline-offset-4"
    >
      {children}
    </a>
  );
}

/** Step three: consents, and whether the fee should be waived. */
function WaiverStep({
  onBack,
  pending = false,
}: {

  onBack: () => void;
  /** Their registration is already with an administrator, so this step is an
   *  edit rather than a submission. */
  pending?: boolean;
}) {
  const submit = useSubmitApplication();
  // Somebody whose membership is already paid for, by a sponsor or themselves,
  // is applying only to be listed: a sponsored newcomer has full access the
  // moment they sign up. There is nothing for them to choose or to waive, so
  // the step asks only for the policies. A money question, not an access one,
  // which is why it reads the tier rather than `hasFullAccess` (an admin has
  // full access on any tier and may still owe a fee).
  const { data: me } = useMe(clerkEnabled);
  const covered = me?.member?.tier === "gift" || me?.member?.tier === "paid";
  // Where they were headed when signing in sent them here (AfterSignInPage
  // passes it along). Null for somebody who came to apply on purpose.
  const [params] = useSearchParams();
  const asked = params.get(REDIRECT_PARAM);
  const returnTo = asked ? safeRedirect(asked) : null;
  const { data: config } = usePublicConfig();
  const price = config?.pricing;
  const standardLabel = price?.totalCents
    ? `Standard, $${(price.totalCents / 100).toFixed(2)} a year`
    : "Standard";

  // Free by default: it is the membership most applicants want, and an
  // unselected pair of buttons reads as a question nobody asked.
  const [tier, setTier] = useState<"free" | "standard">("free");
  const [waive, setWaive] = useState(false);
  const [reason, setReason] = useState("");
  // Only fetched once somebody asks to be covered by an organization: it is a
  // signed-in list of who holds a paid membership, not something to load for
  // every applicant.
  const [claimOrg, setClaimOrg] = useState("");
  const claim = useRequestOrgSponsorship();
  const funded = useFundedOrganizations(waive && reason === WAIVER_REASONS[0]);
  const myFunded = (funded.data?.organizations ?? []).filter((o) => o.yours);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);

  // No money on this form at all. It used to collect a card, which nothing
  // ever charged: approval emails a link to the billing page and the member
  // pays there, so the card bought a "Card on file" note for the reviewer and
  // nothing else. Sponsoring and donating went with it — an approved member is
  // sent to billing, where both are offered beside the membership.
  //
  // Claiming through an organization still needs to name one, otherwise the
  // request is "somebody pays" with nobody in it and there is nothing to
  // approve.
  // Claiming through an organization means naming one. There is no escape for
  // "none of yours is funded": the request would be "somebody pays" with
  // nobody in it, and the note under the empty selector says what to do
  // instead. Submit stayed live through that, which is what let a claim with
  // no legacy on it reach the queue.
  const waiverSettled =
    waive &&
    Boolean(reason) &&
    (reason !== WAIVER_REASONS[0] || Boolean(claimOrg));
  // `tier` starts at "free", so there is always one chosen.
  const ready = terms && privacy && (!waive || waiverSettled);

  if (submit.isSuccess) {
    return (
      <Card className="animate-page-enter mt-4">
        <CardHeader>
          <CheckCircle2 className="animate-item-enter mb-2 size-8 text-green-600" />
          <CardTitle>Registration received</CardTitle>
          <CardDescription>
            Your registration is pending approval by administration. We&apos;ll
            email you when it has been reviewed, and you can keep editing your
            profile in the meantime.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {/* Somebody who clicked a page and was sent through sign-in and
              then through this form gets that page back, rather than being
              left wherever the last step happened to end. */}
          {returnTo && (
            <Button asChild>
              <Link to={returnTo}>Continue</Link>
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/profile">Back to my profile</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-page-enter mt-4">
      <CardHeader>
        <CardTitle className="text-base">Membership</CardTitle>
        <CardDescription className="text-foreground/75 text-sm leading-relaxed">
          {covered
            ? "Your membership is already paid for, so there is nothing to choose or pay here. Accept the two policies to send your registration."
            : "Choose the membership you want and accept the two policies. Nothing is charged here: a standard membership is paid from your billing page once you are approved, and if the fee is a problem you can ask for it to be waived, and an administrator decides."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Two memberships, as buttons rather than a dropdown: it is the one
            choice on this step and it changes what the rest of it asks. */}
        {!covered && (
        <div className="grid gap-3 sm:grid-cols-2">
          {(
            [
              {
                value: "free" as const,
                title: "Free",
                blurb:
                  "A directory listing, the listserv and the resource archive.",
              },
              {
                value: "standard" as const,
                title: standardLabel,
                blurb:
                  "Everything above, plus the full directory, discussions and search.",
              },
            ]
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTier(option.value)}
              aria-pressed={tier === option.value}
              className={
                "rounded-lg border px-3 py-3 text-left text-sm transition-colors " +
                (tier === option.value
                  ? "border-brand bg-primary/10"
                  : "border-border hover:bg-muted")
              }
            >
              <span className="block font-medium">{option.title}</span>
              <span className="text-muted-foreground mt-0.5 block text-xs">
                {option.blurb}
              </span>
            </button>
          ))}
        </div>
        )}

        {!covered && tier === "standard" && (
          <div className="space-y-3 border-t pt-4">
            {/* The waiver first, because it decides what the rest of this
                asks. Below the card it read as an afterthought to a decision
                already made. */}
            <label className="hover:bg-accent/50 flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm">
              <input
                type="checkbox"
                checked={waive}
                onChange={(e) => {
                  setWaive(e.target.checked);
                  if (!e.target.checked) setReason("");
                }}
                className="accent-primary mt-0.5 size-4 shrink-0"
              />
              <span className="font-medium">Waive my membership fee</span>
            </label>

            {waive ? (
              // Two reasons, because they are two different requests and only
              // one of them is a gift. "Someone else pays" is a claim on an
              // organization's membership: it lasts while that organization is
              // funded, and it files a claim an administrator answers with the
              // application. Free text could not tell them apart, so nothing
              // filed a claim at all and the coverage the directory promises
              // was unreachable.
              <div className="space-y-3 pl-3">
                <fieldset className="space-y-2">
                  <legend className="mb-2 text-sm font-medium">Why? *</legend>
                  {WAIVER_REASONS.map((option) => (
                    <label
                      key={option}
                      className="hover:bg-accent/50 flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5 text-sm"
                    >
                      <input
                        type="radio"
                        name="waiver-reason"
                        className="accent-primary mt-0.5 size-4 shrink-0"
                        checked={reason === option}
                        onChange={() => {
                          setReason(option);
                          setClaimOrg("");
                        }}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </fieldset>

                {reason === WAIVER_REASONS[0] && (
                  <div className="space-y-2">
                    <Label htmlFor="claim-org">Which organization? *</Label>
                    {myFunded.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        None of the organizations on your profile has a paid
                        membership yet. Ask a colleague to register and/or
                        invite you first.
                      </p>
                    ) : (
                      <Select value={claimOrg} onValueChange={setClaimOrg}>
                        <SelectTrigger id="claim-org" className="w-full">
                          <SelectValue placeholder="Choose an organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {myFunded.map((o) => (
                            <SelectItem key={o.id} value={String(o.id)}>
                              {o.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        <div className="space-y-2 border-t pt-4">
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>
              I accept the{" "}
              <PolicyLink href={config?.policies?.terms}>Terms of Use</PolicyLink> *
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={privacy}
              onChange={(e) => setPrivacy(e.target.checked)}
              className="accent-primary mt-0.5 size-4 shrink-0"
            />
            <span>
              I accept the{" "}
              <PolicyLink href={config?.policies?.privacy}>Privacy Policy</PolicyLink> *
            </span>
          </label>
        </div>

        {submit.error && (
          <p className="text-destructive text-sm">{submit.error.message}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            disabled={!ready || submit.isPending || claim.isPending}
            onClick={() =>
              submit.mutate(
                {
                  terms_accepted: terms,
                  privacy_accepted: privacy,
                  // "free" for a covered applicant: nothing to charge and
                  // nothing to waive. Approval leaves the tier they hold alone.
                  requested_tier: covered ? "free" : tier,
                  waiver_reason: waive && !covered ? reason : undefined,
                },
                {
                  // The claim rides with the registration rather than being a
                  // second thing to remember: an administrator answers both
                  // with one decision. Filed after the application so a
                  // rejected submission leaves no claim behind.
                  onSuccess: () => {
                    if (waive && reason === WAIVER_REASONS[0] && claimOrg) {
                      claim.mutate({ organization_id: Number(claimOrg) });
                    }
                  },
                }
              )
            }
          >
            {submit.isPending && <Spinner data-slot="icon" />}
            {pending ? "Save changes" : "Submit registration"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
